import React, { useMemo } from 'react';
import { Casa, Tarea, TareaHecha } from '../../types';
import { usePlus } from '../../context/PlusContext';
import { EtiquetaPlus } from '../informes/comun';
import { resumenReparto } from '../../utils/repartoTareas';
import { colorDe, lunesDe, mediodia } from '../../utils/tareas';
import { Scale, Sparkles } from 'lucide-react';

interface Props {
  casa: Casa;
  tareas: Tarea[];
  hechas: TareaHecha[];
  conPlus: boolean;
  /** Si hay más de una casa a la vista, se dice cuál es. */
  conNombre: boolean;
}

/**
 * «Cómo va el reparto» de una casa compartida esta semana: quién ha hecho
 * cuánto (pesado por el esfuerzo), si va equilibrado y lo que viene, contado
 * en frases. Es de Plus; sin él queda una línea que lo cuenta.
 */
export const TarjetaReparto: React.FC<Props> = ({ casa, tareas, hechas, conPlus, conNombre }) => {
  const { ofrecerPlus } = usePlus();
  const reparto = useMemo(() => {
    const hoy = mediodia(new Date());
    return resumenReparto(casa, tareas, hechas, lunesDe(hoy), hoy, hoy);
  }, [casa, tareas, hechas]);

  const titulo = `Cómo va el reparto${conNombre ? ` en ${casa.icono} ${casa.nombre}` : ''}`;

  if (!conPlus) {
    return (
      <button onClick={() => ofrecerPlus('tareasPlus')}
        className="w-full flex items-center gap-2 text-left text-sm px-4 py-3 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-lumbre-400 transition">
        <Scale className="w-4 h-4 text-lumbre-600 shrink-0" />
        <span className="flex-1">{titulo}: quién ha hecho cuánto esta semana, contado en palabras.</span>
        <EtiquetaPlus />
      </button>
    );
  }

  const max = Math.max(1, ...reparto.personas.map(p => p.puntos));
  return (
    <section className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4">
      <h2 className="flex items-center gap-2 font-bold text-gray-900 dark:text-white">
        <Scale className="w-4 h-4 text-lumbre-600" /> <span className="flex-1">{titulo}</span> <EtiquetaPlus />
      </h2>
      {reparto.hechas > 0 && reparto.personas.length > 1 && (
        <ul className="mt-3 space-y-1.5" aria-label="Esfuerzo de cada uno esta semana">
          {reparto.personas.map(p => (
            <li key={p.id} className="flex items-center gap-2 text-xs">
              <span className="w-20 truncate text-gray-700 dark:text-gray-300">{p.nombre}</span>
              <span className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <span className={`block h-full rounded-full ${colorDe(casa, p.id).punto}`} style={{ width: `${(p.puntos / max) * 100}%` }} />
              </span>
              <span className="w-16 text-right tabular-nums text-gray-500 dark:text-gray-400">
                {p.hechas} {p.hechas === 1 ? 'tarea' : 'tareas'}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 space-y-1 text-sm text-gray-700 dark:text-gray-300">
        {reparto.frases.map(f => <p key={f}>{f}</p>)}
      </div>
      <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> Cuenta el esfuerzo de cada tarea, no solo cuántas.
      </p>
    </section>
  );
};
